/*
 * ===========================================================================
 * [StrongGate Limited] [http://www.stronggate.com]
 * Copyright (c) 2026. All rights Reserved.
 * Unauthorized access, use, dissemination, distribution, reproduction of this
 * file, or a portion of, in any form is prohibited and unlawful. If you receive
 * this file, and you are not an authorized company employee, or you believe you
 * have received this file in error, please notify the sender (or originator)
 * and promptly delete this file.
 *
 * Project:
 * File: NotificationService.ts
 * Created by: chan.ying
 * Create Date: 2026-04-27 09:50
 * Version Number: 1.0
 * Version Description:
 *
 * =============================================================================
 */

import ApiUrlBuilder from "@/commons/https/ApiUrlBuilder";
import AppConfig from "@/commons/AppConfig";
import HttpExecutor from "@/commons/https/HttpExecutor";
import UserInboxItemVO from "@/mvc/models/team/UserInboxItemVO";
import Result from "@/commons/Result";
import HttpTokenProvider from "@/commons/https/HttpTokenProvider";
import Log from "@/commons/logs/Log";
import HttpRequestGet from "@/commons/https/HttpRequestGet";
import {buildPageParams, PaginationResponse} from "@/commons/utils/PageUtil";
import {HttpStatus} from "@/commons/https/HttpStatus";
import HttpRequestDelete from "@/commons/https/HttpRequestDelete";
import HttpRequestPut from "@/commons/https/HttpRequestPut";

export class NotificationService {
    public static _instance: NotificationService;

    public static get Instance() {
        return this._instance || (this._instance = new this());
    }

    /**
     * Retrieves a list of user inbox items based on the specified parameters.
     *
     * @param {number} category - The category ID for filtering the user inbox items.
     * @param {number} [isRead] - The read status for filtering (optional).
     * @param {string} [search] - A search query to filter items based on content or metadata (optional).
     * @param {string} [after] - A cursor to fetch items that come after this value (optional).
     * @param {string} [before] - A cursor to fetch items that come before this value (optional).
     * @return {Promise<Result<PaginationResponse<UserInboxItemVO>>>} A promise resolving to the result of the user inbox query, wrapped in a Result object containing pagination details and user inbox item data.
     */
    public list(category?: number[], isRead?: boolean, search?: string, after?: string, before?: string): Promise<Result<PaginationResponse<UserInboxItemVO>>> {
        const params = buildPageParams({
            search: search,
            after: after,
            before: before,
            category: category,
            isRead: isRead,
        })

        let req = new HttpRequestGet(
            ApiUrlBuilder.InstanceTeam,
            "/cld-service-teamsite/api/v1/user-mesg",
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            }, params);

        return new Promise((resolve) => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((rsp) => {
                resolve(Result.of<PaginationResponse<UserInboxItemVO>>(rsp.status, rsp.data));
            }).catch((err) => {
                Log.error("error on query user message, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<PaginationResponse<UserInboxItemVO>>(status));
            });
        });
    }

    /**
     * Retrieves the count of unread messages for a user.
     *
     * @return {Promise<Result<number>>} A promise that resolves to a Result object which contains the status and the number of unread messages.
     */
    public getUnreadCount(): Promise<Result<number>> {
        let req = new HttpRequestGet(
            ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/user-mesg/count`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            });
        return new Promise((resolve) => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((rsp) => {
                resolve(Result.of<number>(rsp.status, rsp.data));
            }).catch((err) => {
                Log.error("error on retrieve user message, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<number>(status));
            });
        });
    }

    /**
     * Retrieves a user inbox item by its unique identifier.
     *
     * @param {string} id - The unique identifier of the user inbox item to retrieve.
     * @return {Promise<Result<UserInboxItemVO>>} A promise resolving to the result object containing the user inbox item or status information.
     */
    public retrieve(id: string): Promise<Result<UserInboxItemVO>> {
        let req = new HttpRequestGet(
            ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/user-mesg/${ApiUrlBuilder.seg(id)}`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            });
        return new Promise((resolve) => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((rsp) => {
                resolve(Result.of<UserInboxItemVO>(rsp.status, rsp.data));
            }).catch((err) => {
                Log.error("error on retrieve user message, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<UserInboxItemVO>(status));
            });
        })
    }

    /**
     * Removes user messages identified by the provided IDs.
     *
     * @param {string[]} ids - An array of IDs corresponding to the user messages to be removed.
     * @return {Promise<Result<void>>} A promise that resolves with a `Result` object indicating the status of the operation.
     */
    public remove(ids: string[]): Promise<Result<void>> {
        let params = new URLSearchParams();
        ids.forEach(s => params.append("ids", s));

        let req = new HttpRequestDelete(
            ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/user-mesg`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            }, null, params);
        return new Promise((resolve) => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((rsp) => {
                resolve(Result.of<void>(rsp.status, rsp.data));
            }).catch((err) => {
                Log.error("error on remove user message, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<void>(status));
            });
        });
    }

    /**
     * Marks the specified messages as read based on the provided IDs.
     *
     * @param {Array<string>} ids - An array of IDs representing the messages to be marked as read.
     * @param isRead -
     * @return {Promise<Result<void>>} A promise containing a Result object indicating the success or failure of the operation.
     */
    public markAsRead(ids: [], isRead: boolean): Promise<Result<void>> {
        let params = new URLSearchParams();
        ids.forEach(s => params.append("ids", s));
        params.append("isRead", String(isRead));

        let req = new HttpRequestPut(
            ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/user-mesg`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            }, null, params);
        return new Promise((resolve) => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((rsp) => {
                resolve(Result.of<void>(rsp.status, rsp.data));
            }).catch((err) => {
                Log.error("error on mark as read for user message, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<void>(status));
            });
        });
    }
}